"use client";

import { useMemo, useState } from "react";
import { Icon } from "./Icons";
import {
  ConfirmDialog,
  EmptyState,
  Field,
  Modal,
  PageHeader,
  StatusPill,
  addDays,
  formatDate,
  formatMoney,
  nightsBetween,
  todayIso
} from "./UI";

function blankReservation(data) {
  const firstRoom = data.rooms.find((room) => room.status === "active");
  const checkIn = todayIso();
  return {
    guestName: "",
    guestEmail: "",
    guestPhone: "",
    roomId: firstRoom?.id || "",
    checkIn,
    checkOut: addDays(checkIn, 1),
    adults: data.settings.defaultAdults || 2,
    children: data.settings.defaultChildren || 0,
    status: "confirmed",
    source: "Booking.com",
    nightlyRate: firstRoom?.targetNet || 0,
    totalAmount: firstRoom?.targetNet || 0,
    notes: ""
  };
}

export default function ReservationsPage({ data, actions }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(() => blankReservation(data));
  const [filter, setFilter] = useState("upcoming");
  const [search, setSearch] = useState("");
  const [removeTarget, setRemoveTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const today = todayIso();

  const reservations = useMemo(() => {
    return [...data.reservations]
      .filter((reservation) => {
        if (filter === "upcoming") {
          return (
            reservation.checkOut >= today &&
            reservation.status !== "cancelled"
          );
        }
        if (filter === "current") {
          return (
            reservation.checkIn <= today &&
            reservation.checkOut > today &&
            reservation.status !== "cancelled"
          );
        }
        if (filter === "past") return reservation.checkOut < today;
        if (filter === "cancelled") return reservation.status === "cancelled";
        return true;
      })
      .filter((reservation) => {
        const query = search.trim().toLowerCase();
        if (!query) return true;
        const room = data.rooms.find((item) => item.id === reservation.roomId);
        return [reservation.guestName, room?.name, room?.code, reservation.source]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(query));
      })
      .sort((a, b) => a.checkIn.localeCompare(b.checkIn));
  }, [data.reservations, data.rooms, filter, search, today]);

  function openCreate() {
    setForm(blankReservation(data));
    setEditing({ id: null });
    setError("");
  }

  function openEdit(reservation) {
    setForm({ ...blankReservation(data), ...reservation });
    setEditing(reservation);
    setError("");
  }

  function update(key) {
    return (event) => {
      const numeric = ["adults", "children", "nightlyRate", "totalAmount"].includes(
        key
      );
      const value = numeric ? Number(event.target.value) : event.target.value;
      setForm((current) => {
        const next = { ...current, [key]: value };
        if (
          ["checkIn", "checkOut", "nightlyRate"].includes(key) &&
          next.checkOut > next.checkIn
        ) {
          next.totalAmount =
            nightsBetween(next.checkIn, next.checkOut) *
            Number(next.nightlyRate || 0);
        }
        if (key === "roomId") {
          const room = data.rooms.find((item) => item.id === value);
          if (room) {
            next.nightlyRate = Number(room.targetNet || 0);
            next.totalAmount =
              nightsBetween(next.checkIn, next.checkOut) * next.nightlyRate;
          }
        }
        return next;
      });
    };
  }

  async function save(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (editing.id) {
        await actions.update(
          "reservations",
          editing.id,
          form,
          "Reservation updated"
        );
      } else {
        await actions.create("reservations", form, "Reservation created");
      }
      setEditing(null);
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSaving(false);
    }
  }

  async function changeStatus(reservation, status) {
    try {
      await actions.update(
        "reservations",
        reservation.id,
        { status },
        status === "checked_in"
          ? "Guest checked in"
          : status === "checked_out"
            ? "Guest checked out"
            : "Reservation updated"
      );
    } catch (statusError) {
      actions.notify(statusError.message, "error");
    }
  }

  return (
    <div className="page">
      <PageHeader
        eyebrow="Front desk"
        title="Reservations"
        text="Create, update and follow every stay without double-booking a room."
        action={
          <button className="button button-primary" onClick={openCreate}>
            <Icon name="plus" size={18} />
            New reservation
          </button>
        }
      />

      <div className="stats-strip reservations-stats">
        <div>
          <span>Arrivals today</span>
          <strong>
            {
              data.reservations.filter(
                (item) =>
                  item.checkIn === today && item.status !== "cancelled"
              ).length
            }
          </strong>
        </div>
        <div>
          <span>In house</span>
          <strong>
            {
              data.reservations.filter(
                (item) =>
                  item.checkIn <= today &&
                  item.checkOut > today &&
                  !["cancelled", "checked_out"].includes(item.status)
              ).length
            }
          </strong>
        </div>
        <div>
          <span>Departures today</span>
          <strong>
            {
              data.reservations.filter(
                (item) =>
                  item.checkOut === today && item.status !== "cancelled"
              ).length
            }
          </strong>
        </div>
      </div>

      <div className="toolbar card">
        <div className="segmented-control">
          {[
            ["upcoming", "Upcoming"],
            ["current", "In house"],
            ["all", "All"],
            ["past", "Past"],
            ["cancelled", "Cancelled"]
          ].map(([value, label]) => (
            <button
              className={filter === value ? "active" : ""}
              key={value}
              onClick={() => setFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>
        <label className="search-field">
          <Icon name="search" size={18} />
          <input
            placeholder="Search guest or room"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
      </div>

      {reservations.length ? (
        <div className="reservation-list">
          {reservations.map((reservation) => {
            const room = data.rooms.find(
              (item) => item.id === reservation.roomId
            );
            const nights = nightsBetween(
              reservation.checkIn,
              reservation.checkOut
            );
            return (
              <article className="reservation-card card" key={reservation.id}>
                <div className="reservation-date">
                  <strong>
                    {new Date(`${reservation.checkIn}T12:00:00`).getDate()}
                  </strong>
                  <span>
                    {new Intl.DateTimeFormat("en", { month: "short" }).format(
                      new Date(`${reservation.checkIn}T12:00:00`)
                    )}
                  </span>
                </div>
                <div className="reservation-main">
                  <div className="reservation-title">
                    <div>
                      <h2>{reservation.guestName}</h2>
                      <span>
                        {room?.code} · {room?.name || "Unknown room"}
                      </span>
                    </div>
                    <StatusPill value={reservation.status} />
                  </div>
                  <div className="reservation-meta">
                    <span>
                      <Icon name="calendar" size={16} />
                      {formatDate(reservation.checkIn, { year: false })} →{" "}
                      {formatDate(reservation.checkOut, { year: false })}
                    </span>
                    <span>
                      <Icon name="moon" size={16} />
                      {nights} {nights === 1 ? "night" : "nights"}
                    </span>
                    <span>
                      <Icon name="user" size={16} />
                      {Number(reservation.adults || 0) +
                        Number(reservation.children || 0)}{" "}
                      guests
                    </span>
                    <span>
                      <Icon name="tag" size={16} />
                      {reservation.source}
                    </span>
                  </div>
                </div>
                <div className="reservation-value">
                  <small>Total</small>
                  <strong>
                    {formatMoney(
                      reservation.totalAmount,
                      data.settings.currency
                    )}
                  </strong>
                </div>
                <div className="reservation-actions">
                  {reservation.status === "confirmed" &&
                  reservation.checkIn <= today ? (
                    <button
                      className="button button-small button-secondary"
                      onClick={() => changeStatus(reservation, "checked_in")}
                    >
                      Check in
                    </button>
                  ) : null}
                  {reservation.status === "checked_in" ? (
                    <button
                      className="button button-small button-secondary"
                      onClick={() => changeStatus(reservation, "checked_out")}
                    >
                      Check out
                    </button>
                  ) : null}
                  <button
                    className="icon-button"
                    onClick={() => openEdit(reservation)}
                    aria-label={`Edit ${reservation.guestName}`}
                  >
                    <Icon name="edit" size={18} />
                  </button>
                  <button
                    className="icon-button icon-button-danger"
                    onClick={() => setRemoveTarget(reservation)}
                    aria-label={`Delete ${reservation.guestName}`}
                  >
                    <Icon name="trash" size={18} />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <EmptyState
          icon="calendar"
          title="No reservations found"
          text="Try another filter or create a new reservation."
          action={
            <button className="button button-primary" onClick={openCreate}>
              New reservation
            </button>
          }
        />
      )}

      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        eyebrow="Reservation"
        title={editing?.id ? "Edit reservation" : "New reservation"}
        wide
      >
        <form onSubmit={save}>
          <div className="form-section">
            <h3>Guest</h3>
            <div className="form-grid">
              <Field label="Guest name">
                <input
                  value={form.guestName}
                  onChange={update("guestName")}
                  placeholder="Full name"
                  required
                />
              </Field>
              <Field label="Booking source">
                <select value={form.source} onChange={update("source")}>
                  <option>Booking.com</option>
                  <option>Direct</option>
                  <option>Phone</option>
                  <option>Walk-in</option>
                  <option>Agency</option>
                  <option>Other</option>
                </select>
              </Field>
              <Field label="Email">
                <input
                  value={form.guestEmail}
                  onChange={update("guestEmail")}
                  type="email"
                  inputMode="email"
                  placeholder="Optional"
                />
              </Field>
              <Field label="Phone">
                <input
                  value={form.guestPhone}
                  onChange={update("guestPhone")}
                  type="tel"
                  inputMode="tel"
                  placeholder="Optional"
                />
              </Field>
            </div>
          </div>

          <div className="form-section">
            <h3>Stay</h3>
            <div className="form-grid">
              <Field label="Room">
                <select
                  value={form.roomId}
                  onChange={update("roomId")}
                  required
                >
                  {data.rooms
                    .filter(
                      (room) =>
                        room.status === "active" || room.id === form.roomId
                    )
                    .map((room) => (
                      <option key={room.id} value={room.id}>
                        {room.code} · {room.name}
                      </option>
                    ))}
                </select>
              </Field>
              <Field label="Status">
                <select value={form.status} onChange={update("status")}>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="checked_in">Checked in</option>
                  <option value="checked_out">Checked out</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </Field>
              <Field label="Check-in">
                <input
                  type="date"
                  value={form.checkIn}
                  onChange={update("checkIn")}
                  required
                />
              </Field>
              <Field
                label="Check-out"
                hint={`${nightsBetween(form.checkIn, form.checkOut)} nights`}
              >
                <input
                  type="date"
                  min={addDays(form.checkIn, 1)}
                  value={form.checkOut}
                  onChange={update("checkOut")}
                  required
                />
              </Field>
              <Field label="Adults">
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={form.adults}
                  onChange={update("adults")}
                />
              </Field>
              <Field label="Children">
                <input
                  type="number"
                  min="0"
                  max="20"
                  value={form.children}
                  onChange={update("children")}
                />
              </Field>
            </div>
          </div>

          <div className="form-section">
            <h3>Price</h3>
            <div className="form-grid">
              <Field label={`Nightly rate (${data.settings.currency})`}>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.nightlyRate}
                  onChange={update("nightlyRate")}
                />
              </Field>
              <Field label={`Stay total (${data.settings.currency})`}>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.totalAmount}
                  onChange={update("totalAmount")}
                />
              </Field>
              <Field className="field-span-2" label="Notes">
                <textarea
                  rows="3"
                  value={form.notes}
                  onChange={update("notes")}
                  placeholder="Arrival details, guest requests, payment notes…"
                />
              </Field>
            </div>
          </div>

          {error ? (
            <div className="inline-alert inline-alert-error">
              <Icon name="warning" />
              {error}
            </div>
          ) : null}
          <div className="modal-actions">
            <button
              type="button"
              className="button button-secondary"
              onClick={() => setEditing(null)}
            >
              Cancel
            </button>
            <button className="button button-primary" disabled={saving}>
              {saving
                ? "Saving…"
                : editing?.id
                  ? "Save reservation"
                  : "Create reservation"}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(removeTarget)}
        title="Delete reservation?"
        text={
          removeTarget
            ? `The reservation for ${removeTarget.guestName} will be permanently removed.`
            : ""
        }
        onClose={() => setRemoveTarget(null)}
        onConfirm={async () => {
          try {
            await actions.remove(
              "reservations",
              removeTarget.id,
              "Reservation deleted"
            );
            setRemoveTarget(null);
          } catch (removeError) {
            actions.notify(removeError.message, "error");
          }
        }}
      />
    </div>
  );
}
